import './scss/UIElements.scss';
import {formatDate, shadeColor} from "./Formats";
import {ReactComponent as StatusIcon} from "./svg/status.svg";
import {ReactComponent as CloseIcon} from "./svg/close.svg";
import {useIntl} from "react-intl";
import {ReactComponent as FreeTextIcon} from "./svg/speech-bubble.svg";
import {ReactComponent as ErrorIcon} from "./svg/error.svg";
import {ReactComponent as SuccessIcon} from "./svg/success.svg";
import {ReactComponent as FailIcon} from "./svg/fail.svg";
import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from "react";
import {ReactComponent as Alert} from "./svg/alert.svg";
import {ReactComponent as LoadingIcon} from "./svg/loading-big.svg";
import {ReactComponent as Chevron} from "./svg/chevron.svg";
import {ReactComponent as SnackError} from "./svg/snack-error.svg";
import {ReactComponent as SnackWarning} from "./svg/snack-warning.svg";
import {ReactComponent as SnackInfo} from "./svg/snack-info.svg";
import {ReactComponent as SnackSuccess} from "./svg/snack-success.svg";
import {ReactComponent as HelpIcon} from "./svg/help.svg";
import {ReactComponent as First} from "./svg/first.svg";
import {ReactComponent as Previous} from "./svg/previous.svg";
import {ReactComponent as Next} from "./svg/next.svg";
import {ReactComponent as Last} from "./svg/last.svg";
import {useSnackbar} from "./Content";
import {GetSelectOptions, GetSurveys, PostCodes} from "./api/APISurvey";
import {createPortal} from "react-dom";
import {AddNewTopic} from "./views/SurveyMessages";
import {useFilters, useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable} from "react-table";
import {useDataContext} from "./views/SurveyContextProvider";
import {useCustomerSegmentData} from "./App";

export function Skeleton( {height, width, circle, inline, noMargin, classText} ) {
    let className = "skeleton"
    if (inline || width) {className = className.concat(' inline')}
    if (circle) {className = className.concat(' circle')}
    if (noMargin) {className = className.concat(' no-margin')}
    if (classText) {className = className.concat(' '+classText)}
    return <span
        className={className}
        style={{height: height, width: width}}
    />
}

export function StatusText({survey}) {
    const intl = useIntl()
    let status
    let statusClass
    if (survey.status === "SCHEDULED") {
        status = intl.formatMessage(
            {
                id: 'main.surveys.status.scheduled',
                defaultMessage: 'Scheduled',
            }
        )
        statusClass = "scheduled"
    }
    if (survey.status === "IN PROGRESS") {
        status = intl.formatMessage(
            {
                id: 'main.surveys.status.inProgress',
                defaultMessage: 'In Progress',
            }
        )
        statusClass = "in-progress"
    }
    if (survey.status === "FINISHED") {
        status = intl.formatMessage(
            {
                id: 'main.surveys.status.finished',
                defaultMessage: 'Finished',
            }
        )
        statusClass = "finished"
    }
    if (survey.status === "DRAFT") {
        status = intl.formatMessage(
            {
                id: 'main.surveys.status.draft',
                defaultMessage: 'Draft',
            }
        )
        statusClass = "draft"
    }
    if (survey.status === "ARCHIVED") {
        status = intl.formatMessage(
            {
                id: 'main.surveys.status.archived',
                defaultMessage: 'Archived',
            }
        )
        statusClass = "draft"
    }
    if (survey.status === "DELETED") {
        status = intl.formatMessage(
            {
                id: 'main.surveys.status.deleted',
                defaultMessage: 'Deleted',
            }
        )
        statusClass = "draft"
    }

    return <p className={"status " + statusClass}>
        <StatusIcon />
        {status}
    </p>
}

export function Popover({closePopover, children, showCloseButton}) {
    const refPopover = useRef(null)
    useEffect(()=>{
        document.addEventListener("mousedown", handleClickOutside)
        function handleClickOutside(event) {
            if (refPopover.current && !refPopover.current.contains(event.target)) {
                closePopover()
            }
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            }
        }
    },[refPopover, closePopover])
    return <>
    <div ref={refPopover} className={"popover"}>
        {showCloseButton &&
            <div className={"close-toggle"} onClick={closePopover}>
                <CloseIcon />
            </div>
        }
        {children}
    </div>
    </>
}

const PopupContext = createContext(null)

export const usePopup = () => useContext(PopupContext)

export const PopupProvider = ({ children }) => {
    const [isPopupOpen, setPopupOpen] = useState(false)
    const [popupContent, setPopupContent] = useState(null)
    const [popupClassName, setPopupClassName] = useState('')

    const showPopup = (content, className = '') => {
        setPopupContent(content)
        setPopupClassName(className)
        setPopupOpen(true)
    }

    const hidePopup = () => {
        setPopupOpen(false)
        setPopupContent(null)
        setPopupClassName('')
    }

    return (
        <PopupContext.Provider value={{ isPopupOpen, popupContent, popupClassName, showPopup, hidePopup }}>
            {children}
        </PopupContext.Provider>
    )
}

export const PopupElement = () => {
    const { isPopupOpen, popupContent, popupClassName, hidePopup } = usePopup()
    const refPopup = useRef(null)

    const adjustPopupOverflow = () => {
        const popup = refPopup.current;
        if (!popup) return;

        const overlayHeight = window.innerHeight - 80;
        const popupHeight = popup.offsetHeight;

        if (popupHeight >= overlayHeight) {
            popup.classList.add('overflow-scroll');
        } else {
            popup.classList.remove('overflow-scroll');
        }
    }

    useEffect(() => {
        const checkIfClickedOutside = e => {
            if (isPopupOpen && refPopup.current && !refPopup.current.contains(e.target)) {
                hidePopup()
            }
        }
        adjustPopupOverflow()
        window.addEventListener('resize', adjustPopupOverflow)

        document.addEventListener("mousedown", checkIfClickedOutside)
        return () => {
            window.removeEventListener('resize', adjustPopupOverflow)
            document.removeEventListener("mousedown", checkIfClickedOutside)
        }

    }, [isPopupOpen, hidePopup])

    if (!isPopupOpen) return null

    return createPortal(
        <div className={"popup-overlay"}>
            <div ref={refPopup} className={`popup ${popupClassName}`}>
                <div className={"close-toggle-container"}>
                    <div className={"close-toggle"} onClick={hidePopup}>
                        <CloseIcon />
                    </div>
                </div>
                {popupContent}
            </div>
        </div>,
        document.body
    )
}

export function Popup({closePopup, children, className}) {
    const refPopup = useRef(null)
    useEffect(()=>{
        document.addEventListener("mousedown", handleClickOutside)
        function handleClickOutside(event) {
            if (refPopup.current && !refPopup.current.contains(event.target)) {
                closePopup()
            }
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            }
        }
    },[refPopup, closePopup])
    return <>
        <div className={"popup-overlay"}>
            <div ref={refPopup} className={className ? className + " popup" : "popup"}>
                <div className={"close-toggle-container"}>
                    <div className={"close-toggle"} onClick={closePopup}>
                        <CloseIcon />
                    </div>
                </div>
                {children}
            </div>
        </div>
    </>
}

export function PopupModal(props) {
    const intl = useIntl()
    return <>
        <h3>{props.header}</h3>
        {props.text &&
            <p>{props.text}</p>
        }
        {props.scrollContent &&
            <div className={"scroll-content"}>
                {props.children}
            </div>
        }
        <div className={"modal-options"}>
            {props.confirmDisabled && props.disabledText &&
                <p className={"alert"}>
                    <Alert />
                    {props.disabledText}
                </p>
            }
            {props.discardText &&
                <button onClick={props.discardAction} className={"discard alert"}>
                    {props.discardText}
                </button>
            }
            <button className={"cancel"} onClick={props.closeModal}>
                {intl.formatMessage(
                    {
                        id: 'modal.cancel',
                        defaultMessage: 'Cancel',
                    })}
            </button>
            {props.confirmDisabled ?
                <button disabled className={props.alert ? "alert confirm" : "confirm"}
                        title={intl.formatMessage(
                            {
                                id: 'modal.checkFields',
                                defaultMessage: 'Check invalid fields',
                            })}>
                    {props.confirmText}
                </button>
                :
                <button onClick={props.confirmAction} className={props.alert ? "alert confirm" : "confirm"}>
                    {props.confirmText}
                </button>
            }
        </div>
    </>
}

export function Modal(props) {
    const intl = useIntl()
    return <div className={props.scrollContent ? "modal-container scrollable" : "modal-container"}>
        <div className={"close-modal"} onClick={props.closeModal}/>
        <div className={"modal"}>
            <div className={"close-toggle-container"}>
                <div className={"close-toggle"} onClick={props.closeModal}>
                    <CloseIcon />
                </div>
            </div>
            <h3>{props.header}</h3>
            {props.text &&
            <p>{props.text}</p>
            }
            {props.scrollContent &&
                <div className={"scroll-content"}>
                    {props.children}
                </div>
            }
            <div className={"modal-options"}>
                {props.confirmDisabled && props.disabledText &&
                    <p className={"alert"}>
                        <Alert />
                        {props.disabledText}
                    </p>
                }
                {props.discardText &&
                <button onClick={props.discardAction} className={"discard alert"}>
                    {props.discardText}
                </button>
                }
                <button className={"cancel"} onClick={props.closeModal}>
                    {intl.formatMessage(
                    {
                        id: 'modal.cancel',
                        defaultMessage: 'Cancel',
                    })}
                </button>
                {props.confirmDisabled ?
                    <button disabled className={props.alert ? "alert confirm" : "confirm"}
                            title={props.alert ? intl.formatMessage(
                        {
                            id: 'modal.checkFields',
                            defaultMessage: 'Check invalid fields',
                        }) : ""}>
                        {props.confirmText}
                    </button>
                    :
                    <button onClick={props.confirmAction} className={props.alert ? "alert confirm" : "confirm"}>
                        {props.confirmText}
                    </button>
                }
            </div>
        </div>
    </div>
}

export function getMessageIcon(props, isBranch, branch) {
    if (props.hasOwnProperty('keyValue')) {
        if ((isBranch && props.condition === ".*") || (props.hasOwnProperty('data') && props.data[props.keyValue].condition === ".*")) {
            return <FreeTextIcon/>
        } else if (isBranch) {
            return props.keyValue.slice(6)
        } else if (props.keyValue === 'other') {
            return <ErrorIcon/>
        } else {
            return props.keyValue
        }
    } else if (branch) {
        if (isBranch && props.condition === ".*") {
            return <FreeTextIcon/>
        } else if (isBranch) {
            return branch.slice(6)
        } else if (branch === 'other') {
            return <ErrorIcon/>
        } else {
            return branch
        }
    }
    return '?'
}

export function getBranchIconColor(props, isBranch, branch) {
    if (isBranch && props.parentColor) {
        return shadeColor(props.parentColor, -props.i*10)
    } else if (isBranch && (props.hasOwnProperty('keyValue') || branch)) {
        switch (props.hasOwnProperty('keyValue') ? props.keyValue.charAt(6) : branch.charAt(6)) {
            case "A":
                return "#8E99E4"
            case "B":
                return "#FF9650"
            case "C":
                return "#FFCE00"
            case "D":
                return "#FD779F"
            case "E":
                return "#007E71"
            case "F":
                return "#00469A"
            case "H":
                return "#8E99E4"
            case "I":
                return "#FF9650"
            case "J":
                return "#FFCE00"
            case "K":
                return "#FD779F"
            case "L":
                return "#007E71"
            case "M":
                return "#00469A"
            default:
                return "#8E99E4"
        }
        /*
       if (props.i % 6 === 0) { return "#8E99E4" }
        if (props.i % 6 === 1) { return "#FF9650" }
        if (props.i % 6 === 2) { return "#FFCE00" }
        if (props.i % 6 === 3) { return "#FD779F" }
        if (props.i % 6 === 4) { return "#007E71" }
        if (props.i % 6 === 5) { return "#00469A" }
        return "#8E99E4"
        */
    } else {
        return "#ADADAD"
    }
}

export function Toast({show, text, status, dismiss, hideToast}) {
    let className = "toast"
    let icon
    if (status === "success") {
        className = className.concat(' success')
        icon = <SuccessIcon/>
    }
    if (status === "alert") {
        className = className.concat(' alert')
        icon = <FailIcon/>
    }

    useEffect(()=> {
        let timeOutDisappear = setTimeout(()=> {
            if (!dismiss) {
                disappear()
            }
        }, 5000)

        return () => {clearTimeout(timeOutDisappear)}
    },[])

    function disappear() {
        /*className = className.concat(' disappear')
        setTimeout(()=> {

        }, 300)*/
        hideToast()
    }

    return <>
        {show &&
        <div className={className}>
            <p>
                {icon &&
                <span className={"icon"}>{icon}</span>
                }
                <span>{text}</span>
            </p>
            {dismiss &&
            <div className={"close"} onClick={()=>disappear()}>
                <CloseIcon />
            </div>
            }
        </div>
        }
    </>
}

export function Snackbar({show, text, state, autoHide}) {
    const {setSnackbarData} = useSnackbar()
    const icon = state === "error" ? <SnackError /> :
            state === "warning" ? <SnackWarning /> :
            state === "success" ? <SnackSuccess /> :
                <SnackInfo />

    const hideSnackbar = <button className={"hide-snackbar"}
             onClick={e=> {
                 e.stopPropagation()
                 setSnackbarData(state => ({
                     ...state,
                     show: false
                 }))
             }}>
            <CloseIcon />
        </button>

    useEffect(()=> {
        let timeOutAutoHide = setTimeout(()=> {
            if (autoHide) {
                setSnackbarData(state => ({
                    ...state,
                    show: false
                }))
            }
        }, 5000)

        return () => {clearTimeout(timeOutAutoHide)}
    },[show, text, state, autoHide])

    return <>
        {show &&
            <div className={"snackbar"} data-state={state}>
                {icon}
                <p>{text}</p>
                {hideSnackbar}
            </div>
        }
    </>
}

export function SlatSkeleton(){
    return <div className={"slat slat-skeleton"}>
        <div className={"slat-header"}>
            <LoadingIcon />
            <h4>
                <Skeleton height={20} width={100} noMargin={true} />
            </h4>
            <p>
                <Skeleton height={20} width={150} noMargin={true} />
            </p>
        </div>
    </div>
}

export function SearchDropdown({options, onSelect, onClose, toggle, left}) {
    const intl = useIntl()
    const [searchText, setSearchText] = useState('')
    const dropdown = useRef(null)
    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside)
        function handleClickOutside(event) {
            if (dropdown.current && !dropdown.current.contains(event.target)  && !toggle.current.contains(event.target) ) {
                onClose()
            }
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            }
        }
    },[dropdown])

    const filteredOptions = options.filter((e) => {
        const formattedText = intl.formatMessage({
            id: `contactmeta.${e}`,
            defaultMessage: e,
        });
        return formattedText.toLowerCase().includes(searchText.toLowerCase());
    })

    return <div className={"search-dropdown"} ref={dropdown} style={{left: left}}>
        <input type={"text"}
               value={searchText}
               autoFocus={true}
               placeholder={intl.formatMessage({id:"search", defaultMessage:"Search..."})}
               onChange={(e) => setSearchText(e.target.value)} />
        <div className="dropdown-options">
            {filteredOptions.map((e, i) => (
                <button key={i} onClick={() => onSelect(e)}>
                    {intl.formatMessage({
                        id: `contactmeta.${e}`,
                        defaultMessage: e,
                    })}
                </button>
            ))}
            {filteredOptions.length === 0 && (
                <div className="no-results">
                    {intl.formatMessage({ id: 'noResults', defaultMessage: 'No results found' })}
                </div>
            )}
        </div>
    </div>
}

export function extractCampaigns(surveys) {
    const campaignSet = new Set()

    surveys.forEach(survey => {
        const campaign = survey.config?.campaign?.trim()
        if (campaign) {
            campaignSet.add(campaign)
        }
    })

    return Array.from(campaignSet)
}

export const CampaignDropdown = ({ surveyData, setSurveyData, campaignName }) => {
    const intl = useIntl()
    const { isLoading, isSuccess, data } = GetSurveys()
    const [campaigns, setCampaigns] = useState([])
    const [filter, setFilter] = useState('')
    const [newCampaignName, setNewCampaignName] = useState('')
    const [addingCampaign, setAddingCampaign] = useState(false)
    const [showDropdown, setShowDropdown] = useState(false)
    const dropdown = useRef()

    useEffect(() => {
        if (data) {
            setCampaigns(extractCampaigns(data))
        }
    }, [data])

    const handleFilterChange = (e) => {
        setFilter(e.target.value)
    }

    const handleCampaignSelect = (campaign) => {
        const nameToSet = campaign === campaignName ? '' : campaign
        const newSurveyData = { ...surveyData }
        newSurveyData.config.campaign = nameToSet
        setSurveyData(newSurveyData)
        setShowDropdown(false)
    }

    const handleAddCampaignClick = () => {
       setAddingCampaign(true)
    }

    const handleNewCampaignNameChange = (e) => {
        setNewCampaignName(e.target.value)
    }

    const handleSubmitNewCampaign = () => {
        setCampaigns([...campaigns, newCampaignName])
        handleCampaignSelect(newCampaignName)
        setNewCampaignName('')
        setAddingCampaign(false)
    }

    const filteredCampaigns = campaigns.filter((campaign) =>
        campaign.toLowerCase().includes(filter.toLowerCase())
    )

    const handleClickOutside = (event) => {
        if (dropdown.current && !dropdown.current.contains(event.target)) {
            setShowDropdown(false)
        }
    }

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside)
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [])

    const [isScrollable, setIsScrollable] = useState(false);
    const campaignsRef = useRef(null)

    useEffect(() => {
        const element = campaignsRef.current;

        if (element) {
            setIsScrollable(element.scrollHeight > element.clientHeight)
        }
    }, [filteredCampaigns, showDropdown, filter])

    const handleScroll = () => {
        const element = campaignsRef.current;
        if (element) {
            setIsScrollable(element.scrollTop + element.clientHeight < element.scrollHeight);
        }
    };

    return (
        <div className={"campaign-dropdown"} ref={dropdown}>
            <div
                className={'campaign-select'}
                onClick={() => setShowDropdown(!showDropdown)}
                tabIndex="0"
                onKeyUp={(e) => e.key === 'Enter' && setShowDropdown(!showDropdown)}
            >
                {campaignName || intl.formatMessage({id:'chooseCampaign', defaultMessage:'Choose a campaign'})}
            </div>
            {showDropdown && (
                <div className='campaign-options'>
                    <input
                        type='text'
                        placeholder={intl.formatMessage({id: 'filterCampaigns', defaultMessage: 'Filter campaigns'})}
                        value={filter}
                        onChange={handleFilterChange}
                        autoFocus
                    />
                    <div className={`${isScrollable ? "show" : ""} scroll-hint-wrapper`}>
                        <div className={'campaigns'} ref={campaignsRef} onScroll={handleScroll}>
                            {isLoading ?
                                <div>
                                    {intl.formatMessage({id:'loading',defaultMessage:'Loading...'})}
                                </div> :
                                filteredCampaigns?.length > 0 ?
                                filteredCampaigns.map((campaign) => (
                                    <div
                                        key={campaign}
                                        className={`campaign-option ${campaign === campaignName ? 'checkmark' : ''}`}
                                        onClick={() => handleCampaignSelect(campaign)}
                                        tabIndex="0"
                                        onKeyUp={(e) => e.key === 'Enter' && handleCampaignSelect(campaign)}
                                    >
                                        {campaign}
                                    </div>
                                )) :
                                    <em className={"grey"}>{intl.formatMessage({id:'noOptionsAvailable', defaultMessage:'No options available'})}</em>
                            }
                        </div>
                    </div>
                    {addingCampaign ? (
                        <div className={"add-campaign"}>
                            <input
                                type='text'
                                placeholder={intl.formatMessage({id:'newCampaignName', defaultMessage:'New campaign name'})}
                                value={newCampaignName}
                                onChange={handleNewCampaignNameChange}
                                autoFocus
                            />
                            <button onClick={handleSubmitNewCampaign}>+ {intl.formatMessage({id:'submitCampaign', defaultMessage:'Submit'})}</button>
                        </div>
                    ) : (
                        <button
                            onClick={handleAddCampaignClick}
                            tabIndex="0"
                            onKeyUp={(e) => e.key === 'Enter' && handleAddCampaignClick()}
                        >
                            + {intl.formatMessage({id:'addCampaign', defaultMessage:'Add campaign'})}</button>
                    )}
                </div>
            )}
        </div>
    )
}

export const UseCaseDropdown = ({ setValue, value }) => {
    const intl = useIntl();
    const [showDropdown, setShowDropdown] = useState(false);
    const [filter, setFilter] = useState('');
    const dropdownRef = useRef();
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [addingUseCase, setAddingUseCase] = useState(false);
    const [newUseCaseTitle, setNewUseCaseTitle] = useState('');
    const [newUseCaseDescription, setNewUseCaseDescription] = useState('');
    const postCodes = PostCodes();
    const { setSnackbarData } = useSnackbar();
    const { value: useCases, setValue: setUseCases } = useDataContext('useCases')

    useEffect(() => {
        setFilteredOptions(
            Object.entries(useCases).filter(([key, useCase]) => {
                const localizedTitle = intl.formatMessage({
                    defaultMessage: useCase?.title || key,
                    id: `usecase.${key}.title`
                })
                return (
                    localizedTitle.toLowerCase().includes(filter.toLowerCase()) || key.toLowerCase().includes(filter.toLowerCase())
                )
            })
        )
    }, [filter, useCases, intl])

    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setShowDropdown(false);
            setAddingUseCase(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const [isScrollable, setIsScrollable] = useState(false);
    const usecasesRef = useRef(null);

    useEffect(() => {
        const element = usecasesRef.current;

        if (element) {
            setIsScrollable(element.scrollHeight > element.clientHeight);
        }
    }, [filteredOptions, showDropdown, filter])

    const handleScroll = () => {
        const element = usecasesRef.current;
        if (element) {
            setIsScrollable(element.scrollTop + element.clientHeight < element.scrollHeight);
        }
    };

    const handleSelect = (useCaseKey) => {
        setValue(useCaseKey);
        setShowDropdown(false);
    };

    const handleAddUseCaseClick = () => {
        setAddingUseCase(true);
    };

    const handleSubmitNewUseCase = () => {
        const customUseCases = Object.keys(useCases).filter((key) => key.startsWith('CUSTOM')).length;
        const newCode = `CUSTOM${String(customUseCases + 1).padStart(3, '0')}`;
        const newUseCase = {
            title: newUseCaseTitle,
            description: newUseCaseDescription,
        };
        const useCaseToPost = {
            codeset: 'usecase',
            code: newCode,
            value: newUseCase,
        }

        const updatedUseCases = {
            ...useCases,
            [newCode]: newUseCase,
        };

        // Optimistically update the state
        setUseCases(updatedUseCases);

        postCodes.mutate(useCaseToPost, {
            onError: error => {
                // If there's an error, rollback the optimistic update
                setUseCases((prevUseCases) => {
                    const { [newCode]: _, ...rest } = prevUseCases; // Remove the added use case
                    return rest;
                });
                setShowDropdown(true);
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage({
                        id: 'addingNewUseCase.failed',
                        defaultMessage: 'Adding new use case failed. Please try again.',
                    }),
                    state: "error",
                    autoHide: true,
                });
                console.error('Failed to add new use case:', error);
            },
            onSuccess: () => {
                // On success, ensure we keep the updated state
                setUseCases(updatedUseCases);
                // Reset input fields and UI state
                setNewUseCaseTitle('');
                setNewUseCaseDescription('');
                setAddingUseCase(false);
                setValue(newCode)

                setSnackbarData({
                    show: true,
                    text: intl.formatMessage({
                        id: 'addingNewUseCase.success',
                        defaultMessage: 'Use case added successfully.',
                    }),
                    state: "success",
                    autoHide: true,
                });
            }
        })

        setShowDropdown(false);
    };

    return (
        <div className="usecase-dropdown" ref={dropdownRef}>
            <div
                className="usecase-select"
                onClick={() => setShowDropdown(!showDropdown)}
                tabIndex="0"
                onKeyUp={(e) => e.key === 'Enter' && setShowDropdown(!showDropdown)}
            >
                {value
                    ? intl.formatMessage({
                        id: `usecase.${value}.title`,
                        defaultMessage: useCases[value]?.title || value,
                    })
                    : intl.formatMessage({
                        id: 'chooseUseCase',
                        defaultMessage: 'Choose a use case',
                    })}
            </div>
            {showDropdown && (
                <div className="usecase-options">
                    <input
                        type="text"
                        placeholder={intl.formatMessage({
                            id: 'filterUseCases',
                            defaultMessage: 'Filter use cases',
                        })}
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        autoFocus
                    />
                    <div className={`${isScrollable ? "show" : ""} scroll-hint-wrapper`}>
                        <div className="usecase-list" ref={usecasesRef} onScroll={handleScroll}>
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map(([useCaseKey, useCase]) => (
                                    <div
                                        key={useCaseKey}
                                        className={`usecase-option ${useCaseKey === value ? 'selected' : ''}`}
                                        onClick={() => handleSelect(useCaseKey)}
                                        tabIndex="0"
                                        onKeyUp={(e) => e.key === 'Enter' && handleSelect(useCaseKey)}
                                        title={intl.formatMessage({
                                            id: `usecase.${useCaseKey}.description`,
                                            defaultMessage: useCase?.description || useCaseKey,
                                        })}
                                    >
                                        {intl.formatMessage({
                                            id: `usecase.${useCaseKey}.title`,
                                            defaultMessage: useCase?.title || useCaseKey,
                                        })}
                                    </div>
                                ))
                            ) : (
                                <em className="grey">
                                    {intl.formatMessage({
                                        id: 'noOptionsAvailable',
                                        defaultMessage: 'No options available',
                                    })}
                                </em>
                            )}
                        </div>
                    </div>
                    {addingUseCase ? (
                        <div className="add-usecase">
                            <input
                                type="text"
                                placeholder={intl.formatMessage({
                                    id: 'newUseCaseTitle',
                                    defaultMessage: 'New use case title',
                                })}
                                value={newUseCaseTitle}
                                onChange={(e) => setNewUseCaseTitle(e.target.value)}
                                autoFocus
                            />
                            <textarea
                                placeholder={intl.formatMessage({
                                    id: 'newUseCaseDescription',
                                    defaultMessage: 'New use case description',
                                })}
                                value={newUseCaseDescription}
                                onChange={(e) => setNewUseCaseDescription(e.target.value)}
                                rows={4}
                            />
                            <button onClick={handleSubmitNewUseCase}>
                                + {intl.formatMessage({id:'submitCampaign', defaultMessage:'Submit'})}
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={handleAddUseCaseClick}
                            tabIndex="0"
                            onKeyUp={(e) => e.key === 'Enter' && handleAddUseCaseClick()}
                        >
                            {intl.formatMessage({
                            id: 'addUseCase',
                            defaultMessage: '+ Add use case',
                        })}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export function Tooltip({ className, text }) {
    const [showTooltip, setShowTooltip] = useState(false)
    return <div className={"tooltip-icon"}
                aria-label={"Tooltip: " + text}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
    >
        <HelpIcon />
        {showTooltip &&
        <div className={`tooltip ${className ? className : ''}`}>
            { text }
        </div>
        }
    </div>
}

export function HoverTooltip({ text, children }) {
    const [showTooltip, setShowTooltip] = useState(false)
    const timerRef = useRef(null);

    const handleMouseEnter = () => {
        timerRef.current = setTimeout(() => {
            setShowTooltip(true)
        }, 300)
    }

    const handleMouseLeave = () => {
        clearTimeout(timerRef.current)
        setShowTooltip(false)
    }

    return <div className={"tooltip-container"}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
    >
        { children }
        {showTooltip &&
            <div className={"tooltip"}>
                { text }
            </div>
        }
    </div>
}

export function SelectComponent({ options }) {
    const intl = useIntl()
    const [value, setValue] = useState(options[0]);
    const selectRef = useRef(null);
    const textMeasureRef = useRef(null);

    useEffect(() => {
        if (selectRef.current && textMeasureRef.current) {
            selectRef.current.style.width = `${textMeasureRef.current.offsetWidth}px`;
        }
    }, [value]);

    return (
        <div>
            <select
                ref={selectRef}
                value={value}
                onChange={e => setValue(e.target.value)}
                style={{position: 'absolute'}}
            >
                {options.map((option, index) => (
                    <option key={index} value={option}>
                        {intl.formatMessage({
                            id: "supporttype."+option,
                            defaultMessage: option
                        })}
                    </option>
                ))}
            </select>
            <div ref={textMeasureRef} style={{display: 'inline-block', visibility: 'hidden'}}>
                {value}
            </div>
        </div>
    );
}

export const Accordion = ({ theme, themeKey, items, onItemClick, isOpen: defaultIsOpen, activeKey, usedCategories, customAddNewTopic }) => {
    const [isOpen, setIsOpen] = useState(true)
    const intl = useIntl()

    useEffect(() => {
        setIsOpen(defaultIsOpen)
    }, [defaultIsOpen])

    return (
        <div className={`${isOpen ? "open" : ""} accordion`}>
            <h2 onClick={() => setIsOpen(!isOpen)}><span className={"toggle"}>{isOpen ? "-" : "+"}</span>{theme}</h2>
            {isOpen &&
            <div className={"contents"}>
                {items.map((item, index) => (
                        <div
                            className={`${item?.key === activeKey ? "active" : usedCategories.includes(item?.key) ? "in-use" : ""} item`}
                            key={index}
                            onClick={() => {
                                onItemClick(item.key)
                            }}
                            title={usedCategories.includes(item?.key) ? intl.formatMessage({id:'supportTopicInUse', defaultMessage:'Support topic is in use'}) : ""}
                        >
                            {item.title}
                        </div>
                    ))}
                {customAddNewTopic || <AddNewTopic onItemClick={onItemClick} theme={themeKey}/>}
            </div>
            }
        </div>
    )
}

export function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  const count = preFilteredRows.length;
  const intl = useIntl()

  return (
    <input
        type={"search"}
      value={filterValue || ''}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
      placeholder={intl.formatMessage({id:'search',defaultMessage:'Search'})}
    />
  );
}

const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
        const defaultRef = React.useRef();
        const resolvedRef = ref || defaultRef;

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate;
        }, [resolvedRef, indeterminate]);

        return <input type="checkbox" ref={resolvedRef} {...rest} />;
    }
);

export function Table({ columns, data, globalSearchPlaceholder, initialSelections, updateSelectedProviders }) {
    const intl = useIntl()
    const defaultColumn = React.useMemo(
        () => ({
            Filter: DefaultColumnFilter,
        }),
        []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        selectedFlatRows,
        toggleRowSelected,
        state: { selectedRowIds, globalFilter, pageIndex, pageSize },
        setGlobalFilter,
    } = useTable(
        {
            columns,
            data,
            defaultColumn,
            getRowId: (row, relativeIndex) => row.id, // use data.id as row id, not row number
            initialState: {
                pageIndex: 0
            },
            // Toggle row selection state from parent component
            useControlledState: state => {
                return React.useMemo(
                    () => ({
                        ...state,
                        selectedRowIds: initialSelections.reduce((prev, curr) => { prev[curr] = true; return prev; }, {}),
                    }),
                    [state, initialSelections]
                )
            },
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination,
        useRowSelect,
        hooks => {
            hooks.visibleColumns.push(columns => [
                {
                    id: 'selection',
                    Header: ({ getToggleAllRowsSelectedProps }) => (
                        <></>
                    ),
                    Cell: ({ row }) => <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />,
                    disableFilters: true,
                },
                ...columns,
            ]);
        }
    );


    return (
        <div>
            <input type={"text"} className={"search"}
                value={globalFilter || ''}
                onChange={e => setGlobalFilter(e.target.value || undefined)}
                placeholder={globalSearchPlaceholder}
                   autoFocus
            />
            <table {...getTableProps()}>
                <thead>
                {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(column => (
                            <th {...column.getHeaderProps()}>
                                <span {...column.getSortByToggleProps({title:undefined})}>
                                        {column.render('Header')}
                                    </span>
                                {column.isSorted &&
                                    <span>
                                    {column.isSortedDesc ?
                                        ' 🔽' :
                                        ' 🔼'
                                    }
                                </span>
                                }
                                {column.canFilter && !column.disableFilters && <div className={column.filterValue ? "filter-active filter-container": "filter-container"}>{column.render('Filter')}</div>}
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                {page.map(row => {
                    prepareRow(row);
                    return (
                        <tr {...row.getRowProps({
                            onClick: () => {
                                updateSelectedProviders(row.id, !row.isSelected)
                            }
                        })}>
                            {row.cells.map(cell => (
                                <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                            ))}
                        </tr>
                    );
                })}
                </tbody>
            </table>
            {rows.length < 1 &&
                <div className={"no-results"}>{intl.formatMessage({id:'noResults', defaultMessage:'No results with given search term'})}</div>
            }
            <div className="pagination">
            <div className={"page-index"}>
                {intl.formatMessage(
                    {
                        id: 'page',
                        defaultMessage: 'Page',
                    })}{' '}
                <strong>
                    {pageIndex + 1}{' '}
                    {intl.formatMessage(
                        {
                            id: 'outOf',
                            defaultMessage: 'of',
                        })}
                    {' '}{pageOptions.length}
                </strong>
            </div>
            <div className={"buttons"}>
                <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                    <First/>
                </button>{' '}
                <button onClick={() => previousPage()} disabled={!canPreviousPage}>
                    <Previous/>
                </button>{' '}
                <button onClick={() => nextPage()} disabled={!canNextPage}>
                    <Next />
                </button>{' '}
                <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
                    <Last/>
                </button>
            </div>
        </div>
        </div>
    )
}

export const WidthVaryingSelect = ({ options, value, onChange, className, disabledOptions = [], optionTexts }) => {
    const textMeasureDivRef = useRef(null)
    const selectRef = useRef(null)

    const handleChange = e => {
        const selectedOption = e.target.value
        onChange(selectedOption)

        // Update the select element width to match the selected option
        const selectedOptionText = textMeasureDivRef.current
        selectedOptionText.textContent = optionTexts(selectedOption)
        const width = Math.ceil(selectedOptionText.getBoundingClientRect().width)
        selectRef.current.style.width = `${width}px`
    }

    useEffect(() => {
        // Set initial width when component mounts
        if (value && options.includes(value)) {
            textMeasureDivRef.current.textContent = optionTexts(value)
            const width = Math.ceil(textMeasureDivRef.current.getBoundingClientRect().width)
            selectRef.current.style.width = `${width}px`
        }
    }, [value, options])

    return (
        <>
            <select ref={selectRef} value={value} onChange={handleChange} className={className}>
                {options.map(option => (
                    <option key={option} value={option} disabled={disabledOptions.includes(option)}>
                        {optionTexts(option)}
                    </option>
                ))}
            </select>
            <div className={"select-width"} ref={textMeasureDivRef} style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }} />
        </>
    )
}

export function ScrollableContainer({ className, children }) {
    const containerRef = useRef(null);
    const posRef = useRef({ top: 0, left: 0, x: 0, y: 0 });

    const mouseDownHandler = (e) => {
        if (e.target.closest('.non-draggable')) {
            return; // Don't do anything if it's non-draggable
        }

        posRef.current = {
            left: containerRef.current.scrollLeft,
            top: containerRef.current.scrollTop,
            x: e.clientX,
            y: e.clientY,
        };

        containerRef.current.style.cursor = 'grabbing';
        containerRef.current.style.userSelect = 'none';

        window.addEventListener('mousemove', mouseMoveHandler);
        window.addEventListener('mouseup', mouseUpHandler);
    };

    const mouseMoveHandler = (e) => {
        const dx = e.clientX - posRef.current.x;
        const dy = e.clientY - posRef.current.y;

        containerRef.current.scrollTop = posRef.current.top - dy;
        containerRef.current.scrollLeft = posRef.current.left - dx;
    };

    const mouseUpHandler = () => {
        if (containerRef.current.style.cursor === 'grabbing') {
            containerRef.current.style.cursor = 'grab';
            containerRef.current.style.removeProperty('user-select');
        }

        window.removeEventListener('mousemove', mouseMoveHandler);
        window.removeEventListener('mouseup', mouseUpHandler);
    };

    useEffect(() => {
        const containerElement = containerRef.current;
        containerElement.style.cursor = 'grab';
        containerElement.addEventListener('mousedown', mouseDownHandler);

        // Cleanup the event listeners when the component unmounts
        return () => {
            containerElement.removeEventListener('mousedown', mouseDownHandler);
            window.removeEventListener('mousemove', mouseMoveHandler);
            window.removeEventListener('mouseup', mouseUpHandler);
        };
    }, []);

    return (
        <div className={`scroll-container ${className}`} ref={containerRef}>
            <div>
                {children}
            </div>
            <div className={"padding"}/>
        </div>
    );
}

export function TagInput({ initialValue = [], onChange, autoFocus, placeholder, value, idKey, duplicateAlert, testRegex, regexAlert, searchContent = [], searchKeys = [], inputIsSearch, tagDisplay, returnArray = false }) {
    const intl = useIntl()
    const [tags, setTags] = useState(initialValue)
    const [inputValue, setInputValue] = useState("")
    const [isFocused, setIsFocused] = useState(false)
    const [showSearchContent, setShowSearchContent] = useState(false)
    const [highlightedTagIndex, setHighlightedTagIndex] = useState(null)
    const inputRef = useRef(null)
    const containerRef = useRef(null)

    const handleInputChange = useCallback((event) => {
            setInputValue(event.target.value)
            setHighlightedTagIndex(null)
        },
        [setInputValue]
    )

    useEffect(()=>{
        // Set initial tag value
        addValue(value, true)
    },[])

    const addValue = (value, skipAlerts = false, setFocusToInput = false) => {
        // Check for duplicates
        if (value && tags.indexOf(value) === -1) {
            // Check for given regex
            if (!testRegex || testRegex.test(value)) {
                setTags([...tags, value])
                setInputValue("")
            } else {
                !skipAlerts && alert(regexAlert)
            }
        } else {
            !skipAlerts && alert(duplicateAlert || intl.formatMessage({defaultMessage:'You have already given this login ID.',id:'alternateLoginIdTaken'}))
        }
        setHighlightedTagIndex(null)
        if (setFocusToInput) {
            if (inputRef.current) {
                inputRef.current.focus()
            }
        }
    }

    const handleKeyDown = useCallback(
        (event) => {
            if (event.key === "Backspace" && inputValue === "") {
                if (highlightedTagIndex !== null) {
                    const newTags = tags.filter((_, index) => index !== highlightedTagIndex);
                    setTags(newTags);
                    setHighlightedTagIndex(null);
                } else if (tags.length > 0) {
                    setHighlightedTagIndex(tags.length - 1);
                }
            } else if (!inputIsSearch && inputValue && [" ", ",", "Tab", "Enter"].includes(event.key)) {
                event.preventDefault();
                const value = inputValue.trim();

                addValue(value)
            } else {
                setHighlightedTagIndex(null)
            }
        },
        [inputValue, tags, highlightedTagIndex]
    )

    const handlePaste = useCallback(
        (event) => {
            event.preventDefault()
            const pastedText = event.clipboardData.getData("text")
            // Handles both commas and spaces
            const pastedTags = pastedText.split(/[\s,]+/).filter(tag => tag.trim().length > 0)

            let duplicateMessages = []
            let regexFailMessages = []
            let newTags = []

            pastedTags.forEach(tag => {
                // Check for duplicates
                if (newTags.includes(tag) || tags.includes(tag)) {
                    duplicateMessages.push(tag);
                } else if (testRegex && !testRegex.test(tag)) {
                    // Check for regex failure
                    regexFailMessages.push(tag);
                } else {
                    newTags.push(tag);
                }
            });

            // Update tags if there are new valid tags
            if (newTags.length > 0) {
                setTags([...tags, ...newTags]);
            }

            let alertMessage = '';
            if (duplicateMessages.length > 0) {
                alertMessage += `${intl.formatMessage({defaultMessage:'You have already given these values:',id:'duplicateValuesAlert'})} ${duplicateMessages.join(', ')}`;
            }
            if (regexFailMessages.length > 0) {
                if (alertMessage.length > 0) alertMessage += '\n'; // Add a new line if there's already a duplicate message
                alertMessage += `${intl.formatMessage({defaultMessage:'Invalid format:',id:'invalidFormatAlert'})} ${regexFailMessages.join(', ')}`;
            }

            if (alertMessage.length > 0) {
                alert(alertMessage);
            }

            setHighlightedTagIndex(null)
        },
        [tags, setTags]
    );

    const handleDelete = useCallback(
        (indexToRemove) => {
            setTags(tags.filter((_, index) => index !== indexToRemove))
        },
        [tags]
    );

    const handleFocus = useCallback(() => {
        setIsFocused(true)
        setShowSearchContent(true)
    }, [])

    const handleBlur = useCallback(() => {
        setIsFocused(false)
    }, [])

    const handleClickOutside = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
            setShowSearchContent(false)
            if (!inputIsSearch && inputValue) {
                const value = inputValue.trim()
                if (value.length > 0) {
                    addValue(value)
                }
            }
        }
    }

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        };
    }, [inputValue])

    useEffect(() => {
        onChange(returnArray ? tags : tags.join(','))
    }, [tags])

    useEffect(() => {
        const span = document.getElementById("hiddenSpan");
        if (span && inputRef.current) {
            const newWidth = `${span.offsetWidth + 8}px`;
            inputRef.current.style.minWidth = newWidth;
        }
    }, [inputValue])

    const filteredSearchContent = searchContent.filter(obj =>
        searchKeys.some(key => {
            const value = obj[key] || ''
            return value.toString().toLowerCase().includes(inputValue.toLowerCase())
        })
    )

    return (
        <div className={"tags-container"} ref={containerRef}>
            <div
                className={`tags-input ${(isFocused || showSearchContent) ? "active" : ""}`}
                onClick={() => inputRef.current && inputRef.current.focus()}
            >
                {tags.map((tag, index) => (
                    <span
                        key={index}
                        className={`tag ${highlightedTagIndex === index ? "highlighted" : ""}`}
                    >
                    {tagDisplay ? tagDisplay(tag) : tag}
                        <button onClick={()=> handleDelete(index)}>
                            <CloseIcon/>
                        </button>
                </span>
                ))}
                <input
                    type="text"
                    ref={inputRef}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    className="tag-input-field"
                    autoFocus={autoFocus}
                    placeholder={tags?.length === 0 ? placeholder : ''}
                />
                <span id="hiddenSpan" style={{ visibility: 'hidden', position: 'absolute', whiteSpace: 'pre' }}>
                {inputValue}
            </span>
            </div>
            {filteredSearchContent?.length > 0 && showSearchContent &&
                <div className={"search-container"}>
                    <table>
                        <tbody>
                        {filteredSearchContent.map((obj,i)=>{
                            const pasteValue = idKey ? idKey : searchKeys[0]
                            const addedValue = obj[pasteValue]
                            if (tags.includes(addedValue)) {
                                return <tr className={"disabled"} key={i}>
                                    {searchKeys.map((key,i) => {
                                        const value = obj[key] || ''
                                        return <td key={i}>
                                            {value}
                                        </td>
                                    })}
                                </tr>
                            }
                            return <tr className={"table-row"} key={i} onClick={()=>addValue(addedValue, false, true)}>
                                {searchKeys.map((key,i) => {
                                    const value = obj[key] || ''
                                    return <td key={i}>
                                        {value}
                                    </td>
                                })}
                            </tr>
                        })}
                        </tbody>
                    </table>
                </div>
            }
        </div>
    )
}

const useOutsideClick = (ref, callback) => {
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                callback();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref, callback]);
};

export const SearchableDropdown = ({ options, idKey, metaKeys, onSelect, value }) => {
    const intl = useIntl()
    const [search, setSearch] = useState('')
    const [selected, setSelected] = useState(value)
    const [isOpen, setIsOpen] = useState(false)
    const [highlightIndex, setHighlightIndex] = useState(-1)
    const dropdownRef = useRef(null)

    useOutsideClick(dropdownRef, () => setIsOpen(false))

    const filteredOptions = options.filter(option =>
        metaKeys.some(key =>
            option?.meta[key] && option?.meta[key].toString().toLowerCase().includes(search.toLowerCase())
        )
    )

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            setHighlightIndex((prevIndex) =>
                prevIndex < filteredOptions.length - 1 ? prevIndex + 1 : prevIndex)
        } else if (e.key === 'ArrowUp') {
            setHighlightIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : 0))
        } else if (e.key === 'Enter' && highlightIndex !== -1) {
            setSelected(filteredOptions[highlightIndex])
            setIsOpen(false)
            setSearch('')
            setHighlightIndex(-1)
        }
    }

    useEffect(() => {
        if (isOpen) {
            setHighlightIndex(-1)
        }
    }, [isOpen])

    const selectOption = (option) => {
        setSelected(option)
        setIsOpen(false)
        setSearch('')
        setHighlightIndex(-1)
        onSelect(option)
    }

    const renderOptionLabel = (option) => {
        const labels = metaKeys.map(key => option?.meta[key])
        if (labels.length > 0 && labels[labels.length - 1]) {
            labels[labels.length - 1] = '– ' + labels[labels.length - 1]
        }
        return labels.join(' ')
    }

    return (
        <div ref={dropdownRef} onKeyDown={handleKeyDown} tabIndex={0} className={"tags-container"}>
            <div onClick={() => setIsOpen(!isOpen)} className={`select-trigger ${isOpen ? "search-open" : ""}`}>
                {selected ?
                    <div className={"selected"}>
                        {renderOptionLabel(options.find(option => option[idKey] === selected))}
                    </div>
                    : 'Select an option'}
            </div>
            {isOpen && (
                <div className={"search-container"}>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={intl.formatMessage({defaultMessage:'Search...', id:'search'})}
                        autoFocus={true}
                    />
                    <table>
                        <tbody>
                        {filteredOptions.map((option, index) => (
                            <tr
                                key={option[idKey]}
                                onClick={() => selectOption(option[idKey])}
                                className={index === highlightIndex ? 'active' : ''}
                            >
                                {metaKeys.map((key,i) => {
                                    const value = option?.meta[key]
                                    return <td key={i}>
                                        {value}
                                    </td>
                                })}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}

export const getContrastColors = (backgroundColor) => {
    if (!backgroundColor) {
        return {
            textColor: '#FFFFFF',
            iconBackground: 'rgba(0, 0, 0, 0.4)'
        }
    }

    // Remove the hash if exists
    const color = backgroundColor.charAt(0) === '#'
        ? backgroundColor.substring(1, 7)
        : backgroundColor;

    const r = parseInt(color.substring(0, 2), 16) || 0;
    const g = parseInt(color.substring(2, 4), 16) || 0;
    const b = parseInt(color.substring(4, 6), 16) || 0;

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Use black text and dark icon background on light backgrounds
    // Use white text and light icon background on dark backgrounds
    return {
        textColor: luminance > 0.5 ? '#000000' : '#FFFFFF',
        iconBackground: luminance > 0.5 ?  'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'
    }
}

export const AutoResizeTextarea = React.forwardRef(({
                                       value,
                                       onChange,
                                       placeholder,
                                       className,
                                       id,
                                       rows = 4,
                                       maxRows = null,
                                       submitOnEnter = false,
                                       onSubmit,
                                       ...otherProps
                                   }, ref) => {
    const internalRef = useRef(null)
    const textareaRef = ref || internalRef;

    const adjustHeight = useCallback(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        // Get computed styles
        const computed = window.getComputedStyle(textarea);
        let lineHeight = parseInt(computed.lineHeight);
        if (isNaN(lineHeight)) {
            const fontSize = parseInt(computed.fontSize);
            lineHeight = Math.floor(fontSize * 1.5);
        }
        const paddingTop = parseInt(computed.paddingTop);
        const paddingBottom = parseInt(computed.paddingBottom);
        const borderTop = parseInt(computed.borderTopWidth);
        const borderBottom = parseInt(computed.borderBottomWidth);
        const boxSizing = computed.boxSizing;

        // Calculate padding and border
        const extraHeight = paddingTop + paddingBottom + (boxSizing === 'border-box' ? borderTop + borderBottom : 0);

        // Calculate min and max heights based on rows
        const minHeight = (lineHeight * rows) + extraHeight;
        const maxHeight = maxRows ? (lineHeight * maxRows) + extraHeight : null;

        // Reset height to get accurate scrollHeight
        textarea.style.height = 'auto';

        // Get the scroll height
        let newHeight = textarea.scrollHeight;

        // For border-box, scrollHeight includes padding but not border
        if (boxSizing === 'border-box') {
            newHeight += borderTop + borderBottom;
        }

        // Apply constraints
        if (newHeight < minHeight) {
            newHeight = minHeight;
            textarea.style.overflowY = 'hidden';
        } else if (maxHeight && newHeight >= maxHeight) {
            // Fix height at maxHeight and enable scrolling
            newHeight = maxHeight;
            textarea.style.overflowY = 'auto';
        } else {
            // Normal auto-resize behavior
            textarea.style.overflowY = 'hidden';
        }

        textarea.style.height = `${newHeight}px`;
    }, [rows, maxRows]);

    // Adjust height when value changes
    useEffect(() => {
        adjustHeight();
    }, [value, adjustHeight]);

    // Adjust height on window resize
    useEffect(() => {
        const handleResize = () => {
            adjustHeight();
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [adjustHeight]);

    const handleChange = (e) => {
        if (onChange) {
            onChange(e);
        }
    };

    const handleInput = () => {
        adjustHeight();
    };

    const handleKeyDown = (e) => {
        if (submitOnEnter && onSubmit && e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSubmit(e);
        }
    };

    return (
        <textarea
            ref={textareaRef}
            id={id}
            value={value}
            onChange={handleChange}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={className}
            rows={rows}
            style={{
                resize: 'none',
                ...otherProps.style
            }}
            {...otherProps}
        />
    );
});